// Calculator logic based on Excel formulas - simplified without tax calculations

export const calculateResults = (inputs) => {
  const {
    revenueType,
    specifiedRevenue,
    avgCustomersPerDay,
    avgRevenuePerCustomer,
    avgPartySize,
    marginInputType,
    directNetMargin,
    cogsType,
    cogsPercentage,
    cogsAmount,
    laborType,
    laborPercentage,
    laborAmount,
    overheadType,
    overheadPercentage,
    overheadAmount,
    loanAmount,
    loanTermMonths,
    loanInterestRate,
    inkindCreditAmount,
    inkindPaybackMonths
  } = inputs;

  // Calculate monthly revenue
  const monthlyRevenue = revenueType === "Specified Monthly Revenue" 
    ? specifiedRevenue 
    : (avgCustomersPerDay * avgRevenuePerCustomer * 30);
  
  const avgDailyRevenue = monthlyRevenue / 30;
  const avgTicketSize = avgRevenuePerCustomer * avgPartySize;
  
  // Calculate net margin - either direct or from cost breakdown
  let netMargin;
  let totalMonthlyCosts = 0;
  let costPerCustomer = 0;
  
  if (marginInputType === "direct") {
    // Use direct net margin input
    netMargin = directNetMargin;
    // Estimate costs from margin for display purposes
    totalMonthlyCosts = monthlyRevenue * (1 - netMargin);
    costPerCustomer = totalMonthlyCosts / (avgCustomersPerDay * 30);
  } else {
    // Calculate from detailed costs
    const cogsCost = cogsType === "Percentage of Revenue" 
      ? (cogsPercentage * monthlyRevenue) 
      : cogsAmount;
    
    const laborCost = laborType === "Percentage of Revenue" 
      ? (laborPercentage * monthlyRevenue) 
      : laborAmount;
    
    const overheadCost = overheadType === "Percentage of Revenue" 
      ? (overheadPercentage * monthlyRevenue) 
      : overheadAmount;
    
    totalMonthlyCosts = cogsCost + laborCost + overheadCost;
    netMargin = 1 - (totalMonthlyCosts / monthlyRevenue);
    costPerCustomer = cogsCost / (avgCustomersPerDay * 30);
  }
  
  // Loan calculations
  const monthlyInterestRate = loanInterestRate / 12;
  const monthlyLoanPayment = loanAmount * 
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, loanTermMonths)) / 
    (Math.pow(1 + monthlyInterestRate, loanTermMonths) - 1);
  
  const dailyLoanCost = (monthlyLoanPayment * 12) / 365;
  // Use NET margin to calculate customers needed for loan repayment
  const customersNeededPerDayLoan = dailyLoanCost / (avgRevenuePerCustomer * netMargin);
  const totalPaidBack = monthlyLoanPayment * loanTermMonths;
  const cogsForLoanCustomers = customersNeededPerDayLoan * costPerCustomer;
  
  // inKind calculations
  const redeemableCredit = inkindCreditAmount * 2;
  const amountPerMonthInkind = redeemableCredit / inkindPaybackMonths;
  const amountPerDayInkind = (amountPerMonthInkind * 12) / 365;
  const customersPerDayInkind = amountPerDayInkind / avgRevenuePerCustomer;
  const cogsForInkindCustomers = customersPerDayInkind * costPerCustomer;
  
  // Calculate additional profit from fewer customers needed
  const customerDifference = customersNeededPerDayLoan - customersPerDayInkind;
  const additionalDailyRevenue = customerDifference * avgRevenuePerCustomer;
  const additionalDailyNetProfit = customerDifference * (avgRevenuePerCustomer * netMargin);
  const additionalMonthlyRevenue = (additionalDailyRevenue * 365) / 12;
  const additionalMonthlyNetProfit = (additionalDailyNetProfit * 365) / 12;
  const additionalYearlyRevenue = additionalMonthlyRevenue * 12;
  const additionalYearlyNetProfit = additionalMonthlyNetProfit * 12;
  
  return {
    restaurant: {
      monthlyRevenue,
      avgDailyRevenue,
      avgTicketSize,
      totalMonthlyCosts,
      netMargin,
      costPerCustomer,
      avgRevenuePerCustomer
    },
    loan: {
      monthlyPayment: monthlyLoanPayment,
      dailyCost: dailyLoanCost,
      customersNeededPerDay: customersNeededPerDayLoan,
      totalPaidBack,
      cogsForCustomers: cogsForLoanCustomers
    },
    inkind: {
      redeemableCredit,
      amountPerMonth: amountPerMonthInkind,
      amountPerDay: amountPerDayInkind,
      customersPerDay: customersPerDayInkind,
      cogsForCustomers: cogsForInkindCustomers,
      additionalDailyRevenue,
      additionalDailyNetProfit,
      additionalMonthlyRevenue,
      additionalMonthlyNetProfit,
      additionalYearlyRevenue,
      additionalYearlyNetProfit
    }
  };
};
